import { z } from 'zod';
import { FieldSchemaZod } from './field.schema.zod';
import { JsonValueZod } from './json.value.zod';
import { RuleConditionZod } from './rule.condition.zod';
import { RuleSchema, RuleSchemaZod } from './rule.schema.zod';

// First-class owned-array repeat. JSON-safe only — no host-library config
// (TanStack etc. lives in code hosts, not schema).
//
// layout:
//   table          — columnar editable grid (invoice lines)
//   section-block  — per-row header/body/footer (quote sections; later)
//   cards          — card list (later)
export const RepeatLayoutZod = z.enum(['table', 'section-block', 'cards']);
export type RepeatLayout = z.infer<typeof RepeatLayoutZod>;

// JSON-safe presentation hints for table hosts. Hosts map these to their
// internal toolkit (TanStack, etc.) — never embed library options here.
export const EntryRowPositionZod = z.enum(['start', 'end']);
export type EntryRowPosition = z.infer<typeof EntryRowPositionZod>;

// How the draft entry row is painted. 'full' = every column (legacy). 'search' =
// only the typeahead/search cell spanning the data columns (designer default for
// catalogue line tables).
export const EntryRowPresentationZod = z.enum(['full', 'search']);
export type EntryRowPresentation = z.infer<typeof EntryRowPresentationZod>;

export const RepeatTableHintsZod = z.object({
  stickyHeader: z.boolean().optional(),
  virtualize: z.boolean().optional(),
  dense: z.boolean().optional(),
  entryRow: z.boolean().optional(),
  // Where the draft entry row sits. 'end' is sticky to the table viewport bottom.
  entryRowPosition: EntryRowPositionZod.optional(),
  // Default 'full' when omitted — keeps existing full-template entry rows.
  entryRowPresentation: EntryRowPresentationZod.optional(),
  entryUnlockWhenPopulated: z.string().min(1).optional(),
  entryEditableBeforeUnlock: z.array(z.string().min(1)).optional(),
  addLabel: z.string().min(1).optional(),
  commands: z.array(z.string().min(1)).optional(),
}).strict();
export type RepeatTableHints = z.infer<typeof RepeatTableHintsZod>;

// One column in a table (or one field in a row template). `bind` is relative
// to the row object (e.g. 'quantity', not 'lines.0.quantity').
export const RepeatColumnSchemaZod = FieldSchemaZod.pick({
  id: true,
  bind: true,
  metadata: true,
  editorInputs: true,
  fieldRenderer: true,
  validators: true,
  required: true,
  debounce: true,
}).extend({
  header: z.string().optional(),
  // Simple layout hint — px number or CSS width string. Not a full size API.
  width: z.union([z.number().positive(), z.string().min(1)]).optional(),
  // Fixed by default. true uses weight 1; a positive number sets the share of
  // remaining width while preserving width as this column's minimum.
  stretch: z.union([z.boolean(), z.number().positive()]).optional(),
  readonly: z.boolean().optional(),
  readonlyWhen: RuleConditionZod.optional(),
  // Row-scoped condition removing the field from that row entirely. Detail fields only: a column
  // is a grid track shared by every row, so hiding it per row would tear holes in the grid.
  // Sibling of readonlyWhen and evaluated the same way — NOT a rule `hide` effect, which repeat
  // rows still do not support (they have no per-row field-instance state).
  hiddenWhen: RuleConditionZod.optional(),
}).strict();
export type RepeatColumnSchema = z.infer<typeof RepeatColumnSchemaZod>;

/** Section variants share one field schema; only defaults, chrome and editability differ. */
export const RepeatProfileSchemaZod = z.object({
  id: z.string().min(1),
  addLabel: z.string().min(1),
  // The single profile without a condition is the fallback for existing rows.
  when: RuleConditionZod.optional(),
  badge: z.string().min(1).optional(),
  itemDefault: z.record(z.string(), JsonValueZod).optional(),
  // Paths relative to this section, e.g. items.sellPrice.
  readonlyFields: z.array(z.string().min(1)).optional(),
}).strict();
export type RepeatProfileSchema = z.infer<typeof RepeatProfileSchemaZod>;

export const RepeatRowActionSchemaZod = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  action: z.string().min(1),
  destructive: z.boolean().optional(),
}).strict();
export type RepeatRowActionSchema = z.infer<typeof RepeatRowActionSchemaZod>;

export type RepeatSchema = {
  id: string;
  // Absolute model path of the array (e.g. 'lines', 'sections'), or relative
  // bind when nested under another repeat (e.g. 'items').
  bind: string;
  layout?: RepeatLayout;
  // Row identity field for track/reorder. Default '_id' (Mongo / Entity).
  trackBy?: string;
  allowAdd?: boolean;
  allowRemove?: boolean;
  allowReorder?: boolean;
  // Seed object merged under a new row on add (plus generated trackBy id).
  itemDefault?: unknown;
  // Runtime capability for dynamic row defaults. Receives itemDefault as its
  // JSON-safe base and returns the row to append.
  rowFactory?: string;
  profiles?: RepeatProfileSchema[];
  rowActions?: RepeatRowActionSchema[];
  columns?: RepeatColumnSchema[];
  // Row-relative fields shown in an expandable panel beneath each real row of a
  // `table` repeat. Same shape as a column because a detail field IS a row field —
  // only its placement differs, so binding, validation and readonly stay one path.
  // `width` / `stretch` are meaningless here; the panel lays fields out itself.
  detail?: RepeatColumnSchema[];
  rules?: RuleSchema[];
  // Nested repeats (e.g. section.items) — relative bind to parent row.
  repeats?: RepeatSchema[];
  table?: RepeatTableHints;
  // Escape hatch: whole-row custom renderer (rare). Prefer columns + layout.
  rowRenderer?: string;
};

export const RepeatSchemaZod: z.ZodType<RepeatSchema> = z.lazy(() => z.object({
  id: z.string().min(1),
  bind: z.string().min(1),
  layout: RepeatLayoutZod.optional(),
  trackBy: z.string().min(1).optional(),
  allowAdd: z.boolean().optional(),
  allowRemove: z.boolean().optional(),
  allowReorder: z.boolean().optional(),
  itemDefault: JsonValueZod.optional(),
  rowFactory: z.string().min(1).optional(),
  profiles: z.array(RepeatProfileSchemaZod).min(1).optional(),
  rowActions: z.array(RepeatRowActionSchemaZod).optional(),
  columns: z.array(RepeatColumnSchemaZod).optional(),
  detail: z.array(RepeatColumnSchemaZod).optional(),
  rules: z.array(RuleSchemaZod).optional(),
  repeats: z.array(RepeatSchemaZod).optional(),
  table: RepeatTableHintsZod.optional(),
  rowRenderer: z.string().optional(),
}).strict());

// Keep FieldSchemaZod referenced so barrel graph tools see the relation.
void FieldSchemaZod;
